import React from "react";
/* =========================================================
   珂玺资本 — Root App + design tokens
   ========================================================= */

const SETTINGS_DEFAULTS = {
  theme: "paper",         // 6 themes，默认米白浅色
  accent: "gold",         // 3 accents
  lang: null,             // null = follow useLang (localStorage 'kexi_lang')
  cnFont: "songti",       // 5 CN fonts
  enFont: "spectral",     // 5 EN fonts
  fontSize: "normal",     // normal | large | xlarge
};

const FONT_SIZES = {
  normal: { cn: "正常", en: "Normal",  scale: 1 },
  large:  { cn: "较大", en: "Large",   scale: 1.12 },
  xlarge: { cn: "最大", en: "Largest", scale: 1.35 },
};

// ---------------------------------------------------------
// THEMES — 3 dark + 3 light, each visually distinct
// ---------------------------------------------------------
const THEMES = {
  // ───── DARK ─────
  navy: {                 // 海军蓝 — saturated cool blue, classical PE / Hillhouse feel
    cn: "海军蓝", en: "Navy", mood: "dark", swatch: "#0d1a36",
    vars: {
      "--bg-0": "#070f22", "--bg-1": "#0d1a36", "--bg-2": "#152648", "--bg-3": "#1f365e",
      "--ivory": "#f5f1e8", "--ivory-2": "#eae4d3",
      "--mute": "rgba(245, 241, 232, 0.62)",
      "--mute-2": "rgba(245, 241, 232, 0.42)",
      "--mute-3": "rgba(245, 241, 232, 0.28)",
      "--line-2": "rgba(245, 241, 232, 0.08)",
    },
  },
  charcoal: {             // 炭墨 — warm amber-brown black, Blackstone luxury PE
    cn: "炭墨", en: "Charcoal", mood: "dark", swatch: "#231a10",
    vars: {
      "--bg-0": "#110c07", "--bg-1": "#231a10", "--bg-2": "#33271a", "--bg-3": "#443524",
      "--ivory": "#f3ece0", "--ivory-2": "#e8e0d0",
      "--mute": "rgba(243, 236, 224, 0.62)",
      "--mute-2": "rgba(243, 236, 224, 0.42)",
      "--mute-3": "rgba(243, 236, 224, 0.28)",
      "--line-2": "rgba(243, 236, 224, 0.08)",
    },
  },
  forest: {               // 深森 — saturated dark green, old-money British wealth
    cn: "深森", en: "Forest", mood: "dark", swatch: "#0a2a1f",
    vars: {
      "--bg-0": "#051a13", "--bg-1": "#0a2a1f", "--bg-2": "#10402f", "--bg-3": "#185942",
      "--ivory": "#f0e8d4", "--ivory-2": "#e4dcc8",
      "--mute": "rgba(240, 232, 212, 0.62)",
      "--mute-2": "rgba(240, 232, 212, 0.42)",
      "--mute-3": "rgba(240, 232, 212, 0.28)",
      "--line-2": "rgba(240, 232, 212, 0.08)",
    },
  },
  // ───── LIGHT ─────
  paper: {                // 米白 — Sequoia editorial, warm cream
    cn: "米白", en: "Paper", mood: "light", swatch: "#f5f1e6",
    vars: {
      "--bg-0": "#ede8d8", "--bg-1": "#f5f1e6", "--bg-2": "#ebe5d2", "--bg-3": "#ddd5be",
      "--ivory": "#1a2238", "--ivory-2": "#2a334a",
      "--mute": "rgba(26, 34, 56, 0.68)",
      "--mute-2": "rgba(26, 34, 56, 0.46)",
      "--mute-3": "rgba(26, 34, 56, 0.28)",
      "--line-2": "rgba(26, 34, 56, 0.1)",
    },
  },
  slate: {                // 冷灰 — McKinsey/BCG modern, cool gray + deep blue text
    cn: "冷灰", en: "Slate", mood: "light", swatch: "#e8e9ec",
    vars: {
      "--bg-0": "#dcdde2", "--bg-1": "#e8e9ec", "--bg-2": "#d8dae0", "--bg-3": "#c4c7d0",
      "--ivory": "#0d1830", "--ivory-2": "#1a2540",
      "--mute": "rgba(13, 24, 48, 0.7)",
      "--mute-2": "rgba(13, 24, 48, 0.48)",
      "--mute-3": "rgba(13, 24, 48, 0.3)",
      "--line-2": "rgba(13, 24, 48, 0.12)",
    },
  },
  porcelain: {            // 浅瓷 — minimalist off-white, contemporary
    cn: "浅瓷", en: "Porcelain", mood: "light", swatch: "#f8f6f0",
    vars: {
      "--bg-0": "#efece3", "--bg-1": "#f8f6f0", "--bg-2": "#eeebde", "--bg-3": "#e2dec8",
      "--ivory": "#2c2a22", "--ivory-2": "#3d3a30",
      "--mute": "rgba(44, 42, 34, 0.7)",
      "--mute-2": "rgba(44, 42, 34, 0.48)",
      "--mute-3": "rgba(44, 42, 34, 0.3)",
      "--line-2": "rgba(44, 42, 34, 0.12)",
    },
  },
};

// Accent palettes — tuned for both dark & light contrast
const ACCENTS = {
  gold:     { cn: "金", en: "Gold",     swatch: "#c9a86b", dark: { "--gold": "#c9a86b", "--gold-soft": "#b89557", "--gold-bright": "#e6c98a", "--gold-dim": "rgba(201, 168, 107, 0.45)", "--line": "rgba(201, 168, 107, 0.18)" }, light: { "--gold": "#9a7c3d", "--gold-soft": "#876a30", "--gold-bright": "#b5945a", "--gold-dim": "rgba(154, 124, 61, 0.55)", "--line": "rgba(154, 124, 61, 0.26)" } },
  cinnabar: { cn: "朱砂", en: "Cinnabar", swatch: "#c46b58", dark: { "--gold": "#c46b58", "--gold-soft": "#a8553f", "--gold-bright": "#d9897a", "--gold-dim": "rgba(196, 107, 88, 0.45)", "--line": "rgba(196, 107, 88, 0.18)" }, light: { "--gold": "#9c4e3c", "--gold-soft": "#85402f", "--gold-bright": "#b66b58", "--gold-dim": "rgba(156, 78, 60, 0.55)", "--line": "rgba(156, 78, 60, 0.26)" } },
  bronze:   { cn: "青铜", en: "Bronze",   swatch: "#a7825a", dark: { "--gold": "#a7825a", "--gold-soft": "#8a6a48", "--gold-bright": "#c19c75", "--gold-dim": "rgba(167, 130, 90, 0.45)", "--line": "rgba(167, 130, 90, 0.18)" }, light: { "--gold": "#7d5d3a", "--gold-soft": "#674a2c", "--gold-bright": "#9b7855", "--gold-dim": "rgba(125, 93, 58, 0.55)", "--line": "rgba(125, 93, 58, 0.26)" } },
};

// ---------------------------------------------------------
// 5 CN fonts + 5 EN fonts
// ---------------------------------------------------------
const CN_FONTS = {
  songti:    { label: "思源宋体",   sample: "稳如山岳",   stack: `"Noto Serif SC", "Source Han Serif SC", "Songti SC", "STSong", serif`,                              sansStack: `"Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif` },
  heiti:     { label: "思源黑体",   sample: "深而非广",   stack: `"Noto Sans SC", "Source Han Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif`,                sansStack: `"Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif` },
  wenkai:    { label: "霞鹜文楷",   sample: "时间作单位", stack: `"LXGW WenKai", "Noto Serif SC", serif`,                                                              sansStack: `"LXGW WenKai", "Noto Sans SC", sans-serif` },
  pingfang:  { label: "苹方 · 现代", sample: "见微知著",   stack: `"PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans SC", sans-serif`,                  sansStack: `"PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans SC", sans-serif` },
  fangsong:  { label: "仿宋 · 公文", sample: "千金一诺",   stack: `"FangSong", "FZFangSong-Z02", "STFangsong", "Noto Serif SC", serif`,                                sansStack: `"FangSong", "FZFangSong-Z02", "STFangsong", "Noto Serif SC", serif` },
};

const EN_FONTS = {
  spectral:  { label: "Spectral",          sample: "Time × density",       type: "serif", stack: `"Spectral", "Source Serif Pro", Georgia, serif`,                  bodyStack: `"Spectral", "Source Serif Pro", Georgia, serif` },
  playfair:  { label: "Playfair Display",  sample: "Patient Capital",      type: "serif", stack: `"Playfair Display", "Spectral", Georgia, serif`,                  bodyStack: `"Playfair Display", "Spectral", Georgia, serif` },
  cormorant: { label: "Cormorant",         sample: "Conviction compounds", type: "serif", stack: `"Cormorant Garamond", "EB Garamond", Georgia, serif`,             bodyStack: `"Cormorant Garamond", "EB Garamond", Georgia, serif` },
  lora:      { label: "Lora",              sample: "Hard tech, long view", type: "serif", stack: `"Lora", "Spectral", Georgia, serif`,                              bodyStack: `"Lora", "Spectral", Georgia, serif` },
  inter:     { label: "Inter",             sample: "Modern. Direct.",      type: "sans",  stack: `"Inter", -apple-system, BlinkMacSystemFont, sans-serif`,          bodyStack: `"Inter", -apple-system, BlinkMacSystemFont, sans-serif` },
};

// 移动模式断点阈值（按正常字体下的视口宽度计算）
const MOBILE_NAV_BREAKPOINT = 1024;

function updateMobileNavFlag(scale) {
  // body 整体 zoom 后，实际等效视口宽度 = innerWidth / scale
  const effective = window.innerWidth / scale;
  document.body.dataset.mobileNav = effective <= MOBILE_NAV_BREAKPOINT ? "1" : "0";
}

// 监听窗口尺寸变化，按当前字号联动切换
if (typeof window !== "undefined" && !window.__kexiResizeBound) {
  window.__kexiResizeBound = true;
  window.addEventListener("resize", () => {
    const raw = localStorage.getItem("kexi_settings");
    const s = raw ? JSON.parse(raw) : { fontSize: "normal" };
    const scale = (window.FONT_SIZES?.[s.fontSize]?.scale) || 1;
    updateMobileNavFlag(scale);
  });
}

function applySettings(s) {
  const root = document.documentElement;
  const theme = THEMES[s.theme] || THEMES.navy;
  Object.entries(theme.vars).forEach(([k, v]) => root.style.setProperty(k, v));
  const accentBase = ACCENTS[s.accent] || ACCENTS.gold;
  const accent = theme.mood === "light" ? accentBase.light : accentBase.dark;
  Object.entries(accent).forEach(([k, v]) => root.style.setProperty(k, v));

  const cn = CN_FONTS[s.cnFont] || CN_FONTS.songti;
  const en = EN_FONTS[s.enFont] || EN_FONTS.spectral;
  const isEn = (s.lang ?? localStorage.getItem("kexi_lang") ?? "cn") === "en";
  if (isEn) {
    root.style.setProperty("--f-serif-cn", en.stack);
    root.style.setProperty("--f-sans-cn", en.bodyStack);
    // serif 字体时 sans 场景（eyebrow、tag 等小字）保持 Inter，Inter 本身则全用
    root.style.setProperty("--f-sans-en", en.type === "sans" ? en.stack : `"Inter", -apple-system, BlinkMacSystemFont, sans-serif`);
  } else {
    // 中文模式：标题和正文统一用同一套字体
    root.style.setProperty("--f-serif-cn", cn.stack);
    root.style.setProperty("--f-sans-cn", cn.stack);
    root.style.setProperty("--f-sans-en", `"Inter", -apple-system, BlinkMacSystemFont, sans-serif`);
  }
  root.style.setProperty("--f-serif-en", en.stack);

  const fs = FONT_SIZES[s.fontSize] || FONT_SIZES.normal;
  root.style.setProperty("--font-scale", fs.scale);
  // body 整体 zoom，nav 也跟着变大
  // min-width: 100vw 防止 zoom 后 body 逻辑宽度缩窄导致居中失效
  document.body.style.zoom = fs.scale;
  document.body.style.minWidth = `${100 / fs.scale}vw`;
  // 清理旧的 main/footer zoom
  const main = document.querySelector("main");
  const footer = document.querySelector("footer");
  if (main) main.style.zoom = "";
  if (footer) footer.style.zoom = "";
  updateMobileNavFlag(fs.scale);

  document.body.dataset.theme = s.theme;
  document.body.dataset.mood = theme.mood;
}

function useSettings() {
  const [s, setS] = React.useState(() => {
    try {
      const raw = localStorage.getItem("kexi_settings");
      return raw ? { ...SETTINGS_DEFAULTS, ...JSON.parse(raw) } : SETTINGS_DEFAULTS;
    } catch { return SETTINGS_DEFAULTS; }
  });
  React.useEffect(() => {
    applySettings(s);
    localStorage.setItem("kexi_settings", JSON.stringify(s));
  }, [s]);
  const setKey = React.useCallback((k, v) => setS(prev => ({ ...prev, [k]: v })), []);
  const resetAll = React.useCallback(() => setS(SETTINGS_DEFAULTS), []);
  return { settings: s, setKey, resetAll };
}

// Expose for SettingsDropdown
Object.assign(window, { THEMES, ACCENTS, CN_FONTS, EN_FONTS, FONT_SIZES });

// ---------------------------------------------------------
// SEO：按当前路由动态更新 <title> / <meta description> / canonical
// （B 档真实路由后，子页有独立 URL，需各自的标题与规范链接）
// ---------------------------------------------------------
const SITE_ORIGIN = "https://kexicapital.net";
const BRAND_SUFFIX = { cn: "珂玺资本 KEXI CAPITAL", en: "KEXI CAPITAL" };
// route 段 → 页面标题
const ROUTE_TITLES = {
  home:      { cn: "耐心资本，陪跑硬科技", en: "Patient Capital for Hard Tech" },
  about:     { cn: "关于我们", en: "About" },
  strategy:  { cn: "投资策略", en: "Strategy" },
  portfolio: { cn: "投资组合", en: "Portfolio" },
  cases:     { cn: "上市案例", en: "IPO Cases" },
  news:      { cn: "动态", en: "Insights" },
  contact:   { cn: "联系我们", en: "Contact" },
  studio:    { cn: "智库", en: "Studio" },
  dashboard: { cn: "用户中心", en: "Dashboard" },
};

function updateSeoHead(route, lang) {
  if (typeof document === "undefined") return;
  const isZh = lang !== "en";
  // 子页(portfolio/<slug>、news/<slug>)取一级段做标题
  const seg = route.split("/")[0];
  const t = ROUTE_TITLES[seg];
  const brand = isZh ? BRAND_SUFFIX.cn : BRAND_SUFFIX.en;
  document.title =
    seg === "home" || !t
      ? `${brand} · ${isZh ? "耐心资本，陪跑硬科技" : "Patient Capital for Hard Tech"}`
      : `${isZh ? t.cn : t.en} · ${brand}`;
  // canonical 指向当前真实路径（不带 query/hash）
  const canonical = `${SITE_ORIGIN}/${route === "home" ? "" : route}`;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = canonical;
  let og = document.querySelector('meta[property="og:url"]');
  if (og) og.content = canonical;
}

function App() {
  const { lang, setLang } = useLang();
  // 初始化路由：从真实路径(pathname)读取，没有则默认 home。
  // 兼容旧 hash 链接(/#/about)：检测到 hash 形式则取出 route 并 replace 成真实路径。
  const initialRoute = (() => {
    if (typeof window === "undefined") return "home";
    const hash = window.location.hash.replace(/^#\/?/, "");
    if (hash) {
      // 旧 hash 链接 → 转真实路径（保留 ?edit=1 等 query）
      window.history.replaceState({ route: hash }, "", `/${hash === "home" ? "" : hash}${window.location.search}`);
      return hash;
    }
    return window.location.pathname.replace(/^\//, "") || "home";
  })();
  const [route, setRouteState] = React.useState(initialRoute);
  const [loginOpen, setLoginOpen] = React.useState(false);
  const { settings, setKey, resetAll } = useSettings();

  // 后台「实时预览」编辑模式：父窗口保存文案后，原地 mutate 已完成，
  // 这里强制重渲染让页面立刻反映新文字（bump 一个无意义的 state）。
  const [, forceTick] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => { window.__kexiForceRerender = forceTick; }, []);

  // 包装 setRoute：导航时 pushState 写入历史(真实路径)，后退按钮可用。
  // 保留 query(如 ?edit=1)，否则 iframe 内导航会掉出实时预览编辑模式。
  const setRoute = React.useCallback((next) => {
    setRouteState(next);
    const path = `/${next === "home" ? "" : next}`;
    const url = path + window.location.search;
    if (window.location.pathname !== path) {
      window.history.pushState({ route: next }, "", url);
    }
  }, []);

  // 监听浏览器后退/前进
  React.useEffect(() => {
    const onPop = () => {
      const r = window.location.pathname.replace(/^\//, "") || "home";
      setRouteState(r);
    };
    window.addEventListener("popstate", onPop);
    // 首次加载若 history 中没有 state，replace 一次确保 popstate 可触发
    if (!window.history.state) {
      window.history.replaceState({ route: initialRoute }, "", `/${initialRoute === "home" ? "" : initialRoute}${window.location.search}`);
    }
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [route]);

  // 路由 / 语言变化时更新页面 SEO head（title / canonical / og:url）
  React.useEffect(() => {
    updateSeoHead(route, lang);
  }, [route, lang]);

  // 切换语言时重新应用字体（英/中模式字体变量不同）
  React.useEffect(() => {
    applySettings({ ...settings, lang });
  }, [lang]);

  React.useEffect(() => {
    document.body.style.overflow = loginOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [loginOpen]);

  const pageProps = { lang, setRoute, onLoginClick: () => setLoginOpen(true) };

  // 项目详情：route 形如 "portfolio/<slug>"
  const portfolioDetailMatch = /^portfolio\/([\w-]+)$/.exec(route);
  // 新闻详情：route 形如 "news/<slug>"
  const newsDetailMatch = /^news\/([\w-]+)$/.exec(route);

  return (
    <>
      <Nav
        route={portfolioDetailMatch ? "portfolio" : (newsDetailMatch ? "news" : route)} setRoute={setRoute}
        lang={lang} setLang={setLang}
        onLoginClick={() => setLoginOpen(true)}
        settings={settings} setSetting={setKey} resetSettings={resetAll}
      />
      <main>
        {portfolioDetailMatch
          ? <ProjectDetailPage {...pageProps} slug={portfolioDetailMatch[1]} />
          : newsDetailMatch
          ? <NewsDetailPage {...pageProps} slug={newsDetailMatch[1]} />
          : <>
              {route === "home" && <HomePage {...pageProps} />}
              {route === "about" && <AboutPage {...pageProps} />}
              {route === "strategy" && <StrategyPage {...pageProps} />}
              {route === "portfolio" && <PortfolioPage {...pageProps} />}
              {route === "cases" && <CasesPage {...pageProps} />}
              {route === "studio" && <StudioPage {...pageProps} />}
              {route === "dashboard" && <window.DashboardPage {...pageProps} />}
              {route === "news" && <NewsPage {...pageProps} />}
              {route === "bp" && <BpPage {...pageProps} />}
              {route === "careers" && <CareersPage {...pageProps} />}
              {route === "contact" && <ContactPage {...pageProps} />}
            </>
        }
      </main>
      <Footer lang={lang} setRoute={setRoute} />
      <AuthModal open={loginOpen} onClose={() => setLoginOpen(false)} lang={lang} />
      <BackToTop lang={lang} backTo={newsDetailMatch ? () => setRoute("news") : null} />
      <GlobalLightbox />
      <ConfirmLogout lang={lang} />
      <ReportOpenModal lang={lang} />
    </>
  );
}


window.__KexiApp = App;
